import { useState } from "react";
import { Button, ButtonGroup, Col, Row } from "react-bootstrap";
import type { CharacterUsage, StageUsage, TournamentTotals } from "../util/tournamentStats";
import { StockIcon } from "./StockIcon";

interface TournamentStatsProps {
  totals: TournamentTotals;
  /** Character usage counted by total games played as each character. */
  characterUsage: CharacterUsage[];
  /** Character usage counted by distinct players (normalized by player). */
  characterByPlayer: CharacterUsage[];
  stageUsage: StageUsage[];
}

function formatDuration(ms: number): string {
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatDateRange(firstMs: number, lastMs: number): string {
  if (!firstMs && !lastMs) return "-";
  const first = new Date(firstMs).toLocaleDateString();
  const last = new Date(lastMs).toLocaleDateString();
  return first === last ? first : `${first} – ${last}`;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Col xs={6} sm={4} className="text-center">
      <div className="fs-4 fw-semibold">{value}</div>
      <div className="text-body-secondary small text-uppercase">{label}</div>
    </Col>
  );
}

// Read-only aggregate stats for a tournament: a totals strip plus character and
// stage usage across every recorded game.
export default function TournamentStats({
  totals,
  characterUsage,
  characterByPlayer,
  stageUsage,
}: TournamentStatsProps) {
  const [charView, setCharView] = useState<"games" | "players">("games");
  const chars = charView === "games" ? characterUsage : characterByPlayer;
  const maxChar = chars[0]?.count ?? 0;
  const maxStage = stageUsage[0]?.count ?? 0;

  return (
    <div className="d-flex flex-column gap-3">
      <div className="bg-body-tertiary rounded p-3">
        <Row className="g-3 justify-content-center">
          <Stat label="Sets" value={totals.sets} />
          <Stat label="Games" value={totals.games} />
          <Stat label="Play time" value={formatDuration(totals.playMs)} />
          <Stat label="Brackets" value={totals.brackets} />
          <Stat label="Date" value={formatDateRange(totals.firstMs, totals.lastMs)} />
        </Row>
      </div>

      <div className="bg-body-tertiary rounded p-3">
        <div className="d-flex justify-content-between align-items-center border-bottom pb-1 mb-1">
          <h6 className="mb-0">Character usage</h6>
          <ButtonGroup size="sm">
            <Button
              variant={charView === "games" ? "secondary" : "outline-secondary"}
              onClick={() => setCharView("games")}
              title="Total games played as each character"
            >
              Games
            </Button>
            <Button
              variant={charView === "players" ? "secondary" : "outline-secondary"}
              onClick={() => setCharView("players")}
              title="Number of players who played each character"
            >
              Players
            </Button>
          </ButtonGroup>
        </div>
        {chars.length === 0 ? (
          <p className="text-muted small">No game data</p>
        ) : (
          <div className="d-flex flex-column gap-1">
            {chars.map((c) => (
              <UsageRow
                key={c.name}
                label={c.name}
                count={c.count}
                max={maxChar}
                // Standard (neutral) stock art here; color-specific icons are
                // reserved for the per-player list.
                icon={<StockIcon character={c.name} size={24} />}
              />
            ))}
          </div>
        )}
      </div>
      <div className="bg-body-tertiary rounded p-3">
        <h6 className="border-bottom pb-1">Stages</h6>
        {stageUsage.length === 0 ? (
          <p className="text-muted small">No game data</p>
        ) : (
          <div className="d-flex flex-column gap-1">
            {stageUsage.map((s) => (
              <UsageRow key={s.stage} label={s.stage} count={s.count} max={maxStage} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UsageRow({
  label,
  count,
  max,
  icon,
}: {
  label: string;
  count: number;
  max: number;
  icon?: React.ReactNode;
}) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className="d-flex align-items-center gap-2">
      {icon}
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex justify-content-between">
          <span className="text-truncate">{label}</span>
          <span className="text-body-secondary ms-2">{count}</span>
        </div>
        <div className="progress" style={{ height: 4 }}>
          <div className="progress-bar" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}
